import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type {
  DestacadoPayload,
  FiguraPayload,
  GlosarioTablaPayload,
  GuideEditorialRuntime,
  SubtituloPayload,
  FormulaDestacadaPayload,
} from '../../core/types/payloads/guide_editorial.types';
import { validateGlosarioTablaPayloadSchema } from '../../core/schemas/guideEditorialSchemas';

function viewRuntime(): GuideEditorialRuntime {
  return { viewed: false };
}

export function validateSubtituloPayload(raw: unknown): SubtituloPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Subtitulo payload must be an object');
  const data = raw as Partial<SubtituloPayload>;
  if (!String(data.title || '').trim()) throw new Error('Subtitulo requires title');
  return {
    title: String(data.title).trim(),
    subtitle: data.subtitle ? String(data.subtitle).trim() : undefined,
  };
}

export function validateDestacadoPayload(raw: unknown): DestacadoPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Destacado payload must be an object');
  const data = raw as Partial<DestacadoPayload>;
  if (!String(data.title || '').trim()) throw new Error('Destacado requires title');
  if (!String(data.body || '').trim()) throw new Error('Destacado requires body');
  const tone =
    data.tone === 'lavender' || data.tone === 'amber' || data.tone === 'neutral' ? data.tone : 'mint';
  const editorialTheme = data.editorialTheme ? String(data.editorialTheme).trim() : undefined;
  return {
    title: String(data.title).trim(),
    body: String(data.body).trim(),
    tone,
    ...(editorialTheme ? { editorialTheme: editorialTheme as DestacadoPayload['editorialTheme'] } : {}),
  };
}

export function validateFiguraPayload(raw: unknown): FiguraPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Figura payload must be an object');
  const data = raw as Partial<FiguraPayload>;
  const altText = String(data.altText || '').trim();
  const caption = String(data.caption || '').trim();
  if (!altText) throw new Error('Figura requires altText');
  if (!caption) throw new Error('Figura requires caption');
  const imagePrompt = String(data.imagePrompt || '').trim() || undefined;
  const wikiQuery = String(data.wikiQuery || '').trim() || undefined;
  const imageUrl = String(data.imageUrl || '').trim() || undefined;
  return {
    title: data.title ? String(data.title).trim() : undefined,
    altText,
    caption,
    ...(imagePrompt ? { imagePrompt } : {}),
    ...(wikiQuery ? { wikiQuery } : {}),
    ...(imageUrl ? { imageUrl } : {}),
  };
}

export function validateGlosarioTablaPayload(raw: unknown): GlosarioTablaPayload {
  return validateGlosarioTablaPayloadSchema(raw);
}

function stripLatexDelimiters(value: string): string {
  return value
    .trim()
    .replace(/^\$\$?/, '')
    .replace(/\$\$?$/, '')
    .replace(/^```(?:latex)?\s*/i, '')
    .replace(/\s*```$/, '')
    .trim();
}

/** Corrige LaTeX mal formado de Mistral (\\FRAC, líneas duplicadas, etc.). */
export function normalizeLatexString(raw: string): string {
  let s = stripLatexDelimiters(raw);
  const lines = s
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length > 1) {
    const valid = lines.find(
      (line) =>
        /\\frac|\\Delta|\\times|\\sum|\\sqrt/i.test(line) &&
        !/\\FRAC|\\DELTA|\\TIMES/.test(line),
    );
    s = valid ?? lines.find((line) => /\\frac/i.test(line)) ?? lines[lines.length - 1]!;
  }

  s = s
    .replace(/\\FRAC/gi, '\\frac')
    .replace(/\\DELTA/gi, '\\Delta')
    .replace(/\\TIMES/gi, '\\times')
    .replace(/\\CDOT/gi, '\\cdot')
    .replace(/\\LEFT/gi, '\\left')
    .replace(/\\RIGHT/gi, '\\right')
    .replace(/\\PERCENT/gi, '\\%')
    .trim();

  return ensureLatexPlainTextWrapped(s);
}

function ensureLatexPlainTextWrapped(latex: string): string {
  let s = latex;
  if (!s) return s;

  s = s.replace(
    /^([A-Za-zÁÉÍÓÚáéíóúÑñ][A-Za-zÁÉÍÓÚáéíóúÑñ\s]{2,}?)\s*(=)/,
    '\\text{$1} $2',
  );
  s = s.replace(
    /(\d+(?:\{,\}\d+)?|\d+(?:\.\d+)?)\s*(cm²|m²|cm|mm|km|kg|ml|m|g)\b(?![a-zA-Z])/gi,
    '$1 \\text{ $2 }',
  );
  s = s.replace(/\\square\s+([A-Za-zÁÉÍÓÚáéíóúñ][A-Za-zÁÉÍÓÚáéíóúñ\s]{2,})/g, '\\square \\text{$1}');

  return s;
}

/** Humanidades: desactivar LaTeX y mostrar estructuras textuales como tipografía editorial. */
export function isHumanitiesFormulaSubject(subjectTag?: string): boolean {
  const tag = String(subjectTag || '').toLowerCase();
  return tag === 'lenguaje' || tag === 'historia';
}

/** Solo ciencias y matemáticas pueden usar bloques formula_destacada con notación algebraica. */
export function isFormulaAllowedSubject(subjectTag?: string): boolean {
  const tag = String(subjectTag || '').toLowerCase();
  return (
    tag === 'matematica' ||
    tag === 'matematicas' ||
    tag.includes('matemat') ||
    tag === 'ciencias' ||
    tag.includes('cienc')
  );
}

/** Convierte pseudo-LaTeX de Mistral en texto legible para Lenguaje/Historia. */
export function humanizeConceptFormulaText(raw: string): string {
  let text = String(raw || '').trim();

  text = text
    .replace(/\\text\{([^}]*)\}/gi, '$1')
    .replace(/\\mathrm\{([^}]*)\}/gi, '$1')
    .replace(/\\[a-zA-Z]+\{([^}]*)\}/g, '$1');

  for (let i = 0; i < 6; i++) {
    const next = text
      .replace(/_\{([^}]*)\}/g, ' $1')
      .replace(/\^\{([^}]*)\}/g, ' $1')
      .replace(/\{([^{}]*)\}/g, '$1');
    if (next === text) break;
    text = next;
  }

  return text
    .replace(/\\[a-zA-Z]+/g, ' ')
    .replace(/\\\\/g, ' ')
    .replace(/\\[+]/g, '')
    .replace(/\$\$?/g, '')
    .replace(/_+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function validateFormulaDestacadaPayload(raw: unknown): FormulaDestacadaPayload {
  if (!raw || typeof raw !== 'object') throw new Error('FormulaDestacada payload must be an object');
  const data = raw as Partial<FormulaDestacadaPayload> & { formula?: string };
  if (!String(data.title || '').trim()) throw new Error('FormulaDestacada requires title');
  const latex = normalizeLatexString(String(data.latex || data.formula || ''));
  if (!latex) throw new Error('FormulaDestacada requires latex');
  const description = String(data.description || '').trim();
  if (!description) throw new Error('FormulaDestacada requires description');
  return {
    title: String(data.title).trim(),
    latex,
    description,
  };
}

export const subtituloLogic: ActivityLogicPlugin<'subtitulo'> = {
  mode: 'subtitulo',
  validatePayload: validateSubtituloPayload,
  createInitialRuntime: viewRuntime,
};

export const destacadoLogic: ActivityLogicPlugin<'destacado'> = {
  mode: 'destacado',
  validatePayload: validateDestacadoPayload,
  createInitialRuntime: viewRuntime,
};

export const figuraLogic: ActivityLogicPlugin<'figura'> = {
  mode: 'figura',
  validatePayload: validateFiguraPayload,
  createInitialRuntime: viewRuntime,
};

export const glosarioTablaLogic: ActivityLogicPlugin<'glosario_tabla'> = {
  mode: 'glosario_tabla',
  validatePayload: validateGlosarioTablaPayload,
  createInitialRuntime: viewRuntime,
};

export const formulaDestacadaLogic: ActivityLogicPlugin<'formula_destacada'> = {
  mode: 'formula_destacada',
  validatePayload: validateFormulaDestacadaPayload,
  createInitialRuntime: viewRuntime,
};
